import { useRef, useState, useMemo } from "react";
import html2canvas from "html2canvas";
import { Download, X, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useCourseData } from "@/hooks/useCourseData";

function getScoreStyle(score, par) {
  if (!score) return {};
  const diff = score - par;
  if (diff <= -2) return { bg: "#FFD700", text: "#000", ring: true };
  if (diff === -1) return { bg: "#E63946", text: "#fff", ring: false };
  if (diff === 0) return { bg: "transparent", text: "#fff", ring: false };
  if (diff === 1) return { bg: "transparent", text: "#94A3B8", ring: false };
  return { bg: "transparent", text: "#64748B", ring: false };
}

function ShareCardModal({ round, onClose }) {
  const [selectedPlayer, setSelectedPlayer] = useState(0);
  const [downloading, setDownloading] = useState(false);
  const [viewMode, setViewMode] = useState("individual");
  const cardRef = useRef(null);
  const leaderboardRef = useRef(null);
  const netRef = useRef(null);
  const { coursesData } = useCourseData();

  const course = round?.course || "Guadalajara Country Club";
  const HOLES = coursesData?.[course] || [];

  // Gross leaderboard
  const rankedByGross = useMemo(() => {
    if (!round?.players?.length || !HOLES.length) return [];
    const withScores = round.players.map(p => {
      const sc = p.scores || [];
      const total = sc.reduce((s, x) => s + x.gross_score, 0);
      const playedPar = HOLES.filter((_, i) => sc.some(s => s.hole_number === i + 1)).reduce((s, h) => s + h.par, 0);
      return { ...p, total, vsPar: total - playedPar, holesPlayed: sc.length };
    });
    withScores.sort((a, b) => a.total - b.total);
    let pos = 1;
    return withScores.map((p, i) => {
      if (i > 0 && p.total > withScores[i - 1].total) pos = i + 1;
      const tied = withScores.filter(x => x.total === p.total).length > 1;
      return { ...p, position: pos, posLabel: tied ? `T${pos}` : `${pos}` };
    });
  }, [round, HOLES]);

  // Net leaderboard (simple: gross - handicap)
  const rankedByNet = useMemo(() => {
    if (!round?.players?.length || !HOLES.length) return [];
    const withScores = round.players.map(p => {
      const sc = p.scores || [];
      const gross = sc.reduce((s, x) => s + x.gross_score, 0);
      const net = gross - (p.handicap || 0);
      return { ...p, gross, net, holesPlayed: sc.length };
    });
    withScores.sort((a, b) => a.net - b.net);
    let pos = 1;
    return withScores.map((p, i) => {
      if (i > 0 && p.net > withScores[i - 1].net) pos = i + 1;
      const tied = withScores.filter(x => x.net === p.net).length > 1;
      return { ...p, position: pos, posLabel: tied ? `T${pos}` : `${pos}` };
    });
  }, [round, HOLES]);

  if (!round || !round.players?.length) return null;
  if (!HOLES.length) return null;

  const players = round.players;
  const player = players[selectedPlayer];
  const scores = player.scores || [];
  const totalGross = scores.reduce((s, sc) => s + sc.gross_score, 0);
  const totalPar = HOLES.filter((_, i) => scores.some(s => s.hole_number === i + 1)).reduce((s, h) => s + h.par, 0);
  const vsPar = totalGross - totalPar;
  const coursePar = HOLES.reduce((s, h) => s + h.par, 0);

  let eagles = 0, birdies = 0, pars = 0, bogeys = 0, doubles = 0, worse = 0;
  scores.forEach(s => {
    const h = HOLES[s.hole_number - 1];
    if (!h) return;
    const diff = s.gross_score - h.par;
    if (diff <= -2) eagles++;
    else if (diff === -1) birdies++;
    else if (diff === 0) pars++;
    else if (diff === 1) bogeys++;
    else if (diff === 2) doubles++;
    else worse++;
  });

  const outScores = scores.filter(s => s.hole_number <= 9);
  const inScores = scores.filter(s => s.hole_number >= 10);
  const outTotal = outScores.reduce((s, sc) => s + sc.gross_score, 0);
  const inTotal = inScores.reduce((s, sc) => s + sc.gross_score, 0);
  const outPar = HOLES.slice(0, 9).filter((_, i) => outScores.some(s => s.hole_number === i + 1)).reduce((s, h) => s + h.par, 0);
  const inPar = HOLES.slice(9).filter((_, i) => inScores.some(s => s.hole_number === i + 10)).reduce((s, h) => s + h.par, 0);

  const handleDownload = async () => {
    const targetRef = viewMode === "individual" ? cardRef : viewMode === "leaderboard" ? leaderboardRef : netRef;
    if (!targetRef.current) return;
    setDownloading(true);
    try {
      const canvas = await html2canvas(targetRef.current, { scale: 3, backgroundColor: null, useCORS: true, logging: false });
      const link = document.createElement("a");
      const suffix = viewMode === "individual" ? player.name.replace(/\s+/g, '_') : viewMode === "leaderboard" ? "leaderboard_gross" : "leaderboard_net";
      link.download = `cadii_${suffix}_${round.date || 'score'}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
    } catch (err) {
      
    }
    setDownloading(false);
  };

  const vsParColor = (v) => v < 0 ? "#E8384F" : v > 0 ? "rgba(255,255,255,0.55)" : "#fff";
  const vsParText = (v) => v === 0 ? "E" : (v > 0 ? `+${v}` : `${v}`);

  // Shared leaderboard header/footer renderer
  const renderLeaderboardHeader = () => (
    <>
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, backgroundImage: "radial-gradient(circle at 15% 85%, rgba(242,208,107,0.06) 0%, transparent 45%), radial-gradient(circle at 85% 15%, rgba(255,255,255,0.03) 0%, transparent 35%)", pointerEvents: "none" }} />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px", position: "relative" }}>
        <img src="/icons/cadii-logo-original.png" alt="cadii" crossOrigin="anonymous" style={{ height: "44px", borderRadius: "8px" }} />
        <div style={{ textAlign: "right" }}>
          <div style={{ color: "#F2D06B", fontSize: "11px", fontWeight: "700", letterSpacing: "1.5px", textTransform: "uppercase" }}>{course.replace(" Country Club", " CC")}</div>
          <div style={{ color: "rgba(255,255,255,0.45)", fontSize: "10px", marginTop: "2px" }}>{round.date || ""} {round.name ? `\u2022 ${round.name}` : ""}</div>
        </div>
      </div>
    </>
  );

  const renderLeaderboardTitle = (title) => (
    <div style={{ textAlign: "center", marginBottom: "18px", position: "relative" }}>
      <div style={{ color: "#F2D06B", fontSize: "13px", fontWeight: "800", letterSpacing: "5px", textTransform: "uppercase" }}>{title}</div>
      <div style={{ width: "50px", height: "2px", background: "linear-gradient(90deg, transparent, #F2D06B, transparent)", margin: "8px auto 0" }} />
    </div>
  );

  const renderLeaderboardFooter = () => (
    <>
      <div style={{ textAlign: "center", marginTop: "12px", padding: "8px 0", borderTop: "1px solid rgba(255,255,255,0.08)" }}>
        <span style={{ color: "rgba(255,255,255,0.3)", fontSize: "10px", letterSpacing: "1px" }}>PAR {coursePar} {"\u2022"} {HOLES.length} HOYOS</span>
      </div>
      <div style={{ textAlign: "center", paddingTop: "6px" }}>
        <div style={{ color: "rgba(255,255,255,0.2)", fontSize: "9px", letterSpacing: "2px" }}>POWERED BY CADII</div>
      </div>
    </>
  );

  const colHeaderStyle = { color: "rgba(242,208,107,0.7)", fontSize: "9px", fontWeight: "700", letterSpacing: "1px" };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="max-w-md w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>

        {/* View toggle */}
        <div className="flex gap-1.5 justify-center mb-3">
          {[
            { key: "individual", label: "Tarjeta" },
            { key: "leaderboard", label: "Gross" },
            { key: "net", label: "Net" },
          ].map(v => (
            <button
              key={v.key}
              onClick={() => setViewMode(v.key)}
              className={`px-4 py-1.5 rounded-full text-sm font-bold transition-all ${viewMode === v.key ? "bg-[#F2D06B] text-[#003A1C]" : "bg-white/15 text-white/70 hover:bg-white/25"}`}
              data-testid={`view-mode-${v.key}`}
            >
              {v.label}
            </button>
          ))}
        </div>

        {/* ===== INDIVIDUAL CARD ===== */}
        {viewMode === "individual" && (
          <>
            <div className="flex items-center justify-between mb-3 px-1">
              <button onClick={() => setSelectedPlayer(Math.max(0, selectedPlayer - 1))} disabled={selectedPlayer === 0} className="text-white disabled:opacity-30">
                <ChevronLeft className="w-6 h-6" />
              </button>
              <p className="text-white font-bold text-sm">{player.name} ({selectedPlayer + 1}/{players.length})</p>
              <button onClick={() => setSelectedPlayer(Math.min(players.length - 1, selectedPlayer + 1))} disabled={selectedPlayer === players.length - 1} className="text-white disabled:opacity-30">
                <ChevronRight className="w-6 h-6" />
              </button>
            </div>
            <div ref={cardRef} style={{ width: "360px", margin: "0 auto", borderRadius: "16px", overflow: "hidden", fontFamily: "'Inter', 'Helvetica', sans-serif" }}>
              <div style={{ background: "linear-gradient(160deg, #0D3328 0%, #1B4D3E 40%, #2A6B55 100%)", padding: "24px 20px", position: "relative" }}>
                <div style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, backgroundImage: "radial-gradient(circle at 20% 80%, rgba(212,163,115,0.08) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(255,255,255,0.04) 0%, transparent 40%)", pointerEvents: "none" }} />
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px", position: "relative" }}>
                  <img src="/icons/cadii-logo-original.png" alt="cadii" crossOrigin="anonymous" style={{ height: "48px", borderRadius: "10px" }} />
                  <div style={{ textAlign: "right" }}>
                    <div style={{ color: "#D4A373", fontSize: "11px", fontWeight: "600", letterSpacing: "1px", textTransform: "uppercase" }}>{course.replace(" Country Club", " CC")}</div>
                    <div style={{ color: "rgba(255,255,255,0.5)", fontSize: "10px", marginTop: "2px" }}>{round.date || ""}</div>
                  </div>
                </div>
                <div style={{ textAlign: "center", marginBottom: "20px", position: "relative" }}>
                  <div style={{ color: "rgba(255,255,255,0.5)", fontSize: "11px", letterSpacing: "2px", textTransform: "uppercase", marginBottom: "4px" }}>Scorecard</div>
                  <div style={{ color: "#fff", fontSize: "28px", fontWeight: "800", letterSpacing: "-0.5px" }}>{player.name}</div>
                  <div style={{ color: "rgba(255,255,255,0.4)", fontSize: "11px", marginTop: "2px" }}>HCP {player.handicap}</div>
                  <div style={{ marginTop: "12px", display: "flex", justifyContent: "center", gap: "20px" }}>
                    <div style={{ textAlign: "center" }}>
                      <div style={{ color: "#D4A373", fontSize: "36px", fontWeight: "800", lineHeight: "1" }}>{totalGross || "-"}</div>
                      <div style={{ color: "rgba(255,255,255,0.4)", fontSize: "10px", marginTop: "2px" }}>GROSS</div>
                    </div>
                    <div style={{ width: "1px", background: "rgba(255,255,255,0.15)" }} />
                    <div style={{ textAlign: "center" }}>
                      <div style={{ color: vsPar < 0 ? "#4ADE80" : vsPar > 0 ? "#F87171" : "#fff", fontSize: "36px", fontWeight: "800", lineHeight: "1" }}>
                        {vsPar === 0 ? "E" : (vsPar > 0 ? `+${vsPar}` : vsPar)}
                      </div>
                      <div style={{ color: "rgba(255,255,255,0.4)", fontSize: "10px", marginTop: "2px" }}>VS PAR</div>
                    </div>
                  </div>
                </div>
                {/* OUT */}
                <div style={{ background: "rgba(0,0,0,0.25)", borderRadius: "10px", padding: "8px", marginBottom: "6px" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr>
                        <td style={{ color: "rgba(255,255,255,0.4)", fontSize: "9px", padding: "2px 4px", fontWeight: "600" }}>HOLE</td>
                        {HOLES.slice(0, 9).map(h => <td key={h.hole} style={{ color: "rgba(255,255,255,0.5)", fontSize: "9px", textAlign: "center", padding: "2px 0", fontWeight: "600" }}>{h.hole}</td>)}
                        <td style={{ color: "#D4A373", fontSize: "9px", textAlign: "center", padding: "2px 4px", fontWeight: "700" }}>OUT</td>
                      </tr>
                      <tr>
                        <td style={{ color: "rgba(255,255,255,0.3)", fontSize: "9px", padding: "2px 4px" }}>PAR</td>
                        {HOLES.slice(0, 9).map(h => <td key={h.hole} style={{ color: "rgba(255,255,255,0.3)", fontSize: "9px", textAlign: "center", padding: "2px 0" }}>{h.par}</td>)}
                        <td style={{ color: "rgba(212,163,115,0.6)", fontSize: "9px", textAlign: "center", padding: "2px 4px" }}>{outPar || ""}</td>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td style={{ color: "#fff", fontSize: "10px", padding: "4px 4px", fontWeight: "600" }}>SCR</td>
                        {HOLES.slice(0, 9).map(h => {
                          const sc = scores.find(s => s.hole_number === h.hole);
                          const st = sc ? getScoreStyle(sc.gross_score, h.par) : {};
                          return (
                            <td key={h.hole} style={{ textAlign: "center", padding: "2px 0" }}>
                              <div style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: "22px", height: "22px", borderRadius: "50%", background: st.bg || "transparent", color: st.text || "rgba(255,255,255,0.3)", fontSize: "11px", fontWeight: "700", border: sc && sc.gross_score - h.par === -1 ? "2px solid #E63946" : sc && sc.gross_score - h.par <= -2 ? "2px solid #FFD700" : "none", boxShadow: sc && sc.gross_score - h.par <= -2 ? "0 0 6px rgba(255,215,0,0.4)" : "none" }}>
                                {sc ? sc.gross_score : "-"}
                              </div>
                            </td>
                          );
                        })}
                        <td style={{ color: "#D4A373", fontSize: "12px", textAlign: "center", fontWeight: "800", padding: "2px 4px" }}>{outTotal || "-"}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                {/* IN */}
                <div style={{ background: "rgba(0,0,0,0.25)", borderRadius: "10px", padding: "8px", marginBottom: "16px" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr>
                        <td style={{ color: "rgba(255,255,255,0.4)", fontSize: "9px", padding: "2px 4px", fontWeight: "600" }}>HOLE</td>
                        {HOLES.slice(9).map(h => <td key={h.hole} style={{ color: "rgba(255,255,255,0.5)", fontSize: "9px", textAlign: "center", padding: "2px 0", fontWeight: "600" }}>{h.hole}</td>)}
                        <td style={{ color: "#D4A373", fontSize: "9px", textAlign: "center", padding: "2px 4px", fontWeight: "700" }}>IN</td>
                      </tr>
                      <tr>
                        <td style={{ color: "rgba(255,255,255,0.3)", fontSize: "9px", padding: "2px 4px" }}>PAR</td>
                        {HOLES.slice(9).map(h => <td key={h.hole} style={{ color: "rgba(255,255,255,0.3)", fontSize: "9px", textAlign: "center", padding: "2px 0" }}>{h.par}</td>)}
                        <td style={{ color: "rgba(212,163,115,0.6)", fontSize: "9px", textAlign: "center", padding: "2px 4px" }}>{inPar || ""}</td>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td style={{ color: "#fff", fontSize: "10px", padding: "4px 4px", fontWeight: "600" }}>SCR</td>
                        {HOLES.slice(9).map(h => {
                          const sc = scores.find(s => s.hole_number === h.hole);
                          const st = sc ? getScoreStyle(sc.gross_score, h.par) : {};
                          return (
                            <td key={h.hole} style={{ textAlign: "center", padding: "2px 0" }}>
                              <div style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: "22px", height: "22px", borderRadius: "50%", background: st.bg || "transparent", color: st.text || "rgba(255,255,255,0.3)", fontSize: "11px", fontWeight: "700", border: sc && sc.gross_score - h.par === -1 ? "2px solid #E63946" : sc && sc.gross_score - h.par <= -2 ? "2px solid #FFD700" : "none", boxShadow: sc && sc.gross_score - h.par <= -2 ? "0 0 6px rgba(255,215,0,0.4)" : "none" }}>
                                {sc ? sc.gross_score : "-"}
                              </div>
                            </td>
                          );
                        })}
                        <td style={{ color: "#D4A373", fontSize: "12px", textAlign: "center", fontWeight: "800", padding: "2px 4px" }}>{inTotal || "-"}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                {/* Stats */}
                <div style={{ display: "flex", justifyContent: "center", gap: "6px", marginBottom: "16px", flexWrap: "wrap" }}>
                  {eagles > 0 && <div style={{ background: "rgba(255,215,0,0.15)", borderRadius: "8px", padding: "6px 10px", textAlign: "center" }}><div style={{ color: "#FFD700", fontSize: "18px", fontWeight: "800", lineHeight: "1" }}>{eagles}</div><div style={{ color: "rgba(255,215,0,0.7)", fontSize: "8px", letterSpacing: "0.5px" }}>EAGLE</div></div>}
                  {birdies > 0 && <div style={{ background: "rgba(230,57,70,0.15)", borderRadius: "8px", padding: "6px 10px", textAlign: "center" }}><div style={{ color: "#E63946", fontSize: "18px", fontWeight: "800", lineHeight: "1" }}>{birdies}</div><div style={{ color: "rgba(230,57,70,0.7)", fontSize: "8px", letterSpacing: "0.5px" }}>BIRDIE</div></div>}
                  <div style={{ background: "rgba(255,255,255,0.08)", borderRadius: "8px", padding: "6px 10px", textAlign: "center" }}><div style={{ color: "#fff", fontSize: "18px", fontWeight: "800", lineHeight: "1" }}>{pars}</div><div style={{ color: "rgba(255,255,255,0.4)", fontSize: "8px", letterSpacing: "0.5px" }}>PAR</div></div>
                  {bogeys > 0 && <div style={{ background: "rgba(255,255,255,0.05)", borderRadius: "8px", padding: "6px 10px", textAlign: "center" }}><div style={{ color: "#94A3B8", fontSize: "18px", fontWeight: "800", lineHeight: "1" }}>{bogeys}</div><div style={{ color: "rgba(255,255,255,0.3)", fontSize: "8px", letterSpacing: "0.5px" }}>BOGEY</div></div>}
                  {(doubles + worse) > 0 && <div style={{ background: "rgba(255,255,255,0.03)", borderRadius: "8px", padding: "6px 10px", textAlign: "center" }}><div style={{ color: "#64748B", fontSize: "18px", fontWeight: "800", lineHeight: "1" }}>{doubles + worse}</div><div style={{ color: "rgba(255,255,255,0.2)", fontSize: "8px", letterSpacing: "0.5px" }}>DBL+</div></div>}
                </div>
                <div style={{ textAlign: "center", borderTop: "1px solid rgba(255,255,255,0.08)", paddingTop: "12px" }}>
                  <div style={{ color: "rgba(255,255,255,0.25)", fontSize: "9px", letterSpacing: "2px" }}>POWERED BY CADII</div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* ===== GROSS LEADERBOARD ===== */}
        {viewMode === "leaderboard" && (
          <div ref={leaderboardRef} data-testid="leaderboard-card" style={{ width: "360px", margin: "0 auto", borderRadius: "16px", overflow: "hidden", fontFamily: "'Inter', 'Helvetica', sans-serif" }}>
            <div style={{ background: "linear-gradient(170deg, #003A1C 0%, #00552B 50%, #006B35 100%)", padding: "24px 16px 16px", position: "relative" }}>
              {renderLeaderboardHeader()}
              {renderLeaderboardTitle("LEADERBOARD")}

              {/* Column headers */}
              <div style={{ display: "flex", alignItems: "center", padding: "8px 12px", borderBottom: "2px solid rgba(242,208,107,0.25)", borderLeft: "3px solid transparent" }}>
                <div style={{ width: "29px", ...colHeaderStyle }}>POS</div>
                <div style={{ flex: 1, ...colHeaderStyle }}>JUGADOR</div>
                <div style={{ width: "48px", textAlign: "center", ...colHeaderStyle }}>TOTAL</div>
                <div style={{ width: "48px", textAlign: "center", ...colHeaderStyle }}>PAR</div>
              </div>

              {/* Player rows */}
              {rankedByGross.map((p, i) => {
                const isLeader = p.position === 1;
                return (
                  <div key={p.player_id} data-testid={`leaderboard-row-${i}`} style={{
                    display: "flex", alignItems: "center", padding: "11px 12px",
                    background: i % 2 === 0 ? "rgba(0,0,0,0.18)" : "rgba(0,0,0,0.10)",
                    borderBottom: i < rankedByGross.length - 1 ? "1px solid rgba(255,255,255,0.06)" : "none",
                    borderLeft: isLeader ? "3px solid #F2D06B" : "3px solid transparent",
                  }}>
                    <div style={{ width: "29px", color: isLeader ? "#F2D06B" : "rgba(255,255,255,0.6)", fontSize: "14px", fontWeight: "800" }}>{p.posLabel}</div>
                    <div style={{ flex: 1, color: isLeader ? "#fff" : "rgba(255,255,255,0.85)", fontSize: "13px", fontWeight: isLeader ? "700" : "500", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", paddingRight: "4px" }}>{p.name}</div>
                    <div style={{ width: "48px", textAlign: "center", color: "#fff", fontSize: "16px", fontWeight: "800" }}>{p.total || "-"}</div>
                    <div style={{ width: "48px", textAlign: "center", color: vsParColor(p.vsPar), fontSize: "16px", fontWeight: "800" }}>{p.holesPlayed > 0 ? vsParText(p.vsPar) : "-"}</div>
                  </div>
                );
              })}

              {renderLeaderboardFooter()}
            </div>
          </div>
        )}

        {/* ===== NET (HCP) LEADERBOARD ===== */}
        {viewMode === "net" && (
          <div ref={netRef} data-testid="net-leaderboard-card" style={{ width: "360px", margin: "0 auto", borderRadius: "16px", overflow: "hidden", fontFamily: "'Inter', 'Helvetica', sans-serif" }}>
            <div style={{ background: "linear-gradient(170deg, #1A1A2E 0%, #16213E 50%, #0F3460 100%)", padding: "24px 16px 16px", position: "relative" }}>
              {/* Pattern overlay */}
              <div style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, backgroundImage: "radial-gradient(circle at 15% 85%, rgba(212,163,115,0.06) 0%, transparent 45%), radial-gradient(circle at 85% 15%, rgba(255,255,255,0.03) 0%, transparent 35%)", pointerEvents: "none" }} />

              {/* Header */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px", position: "relative" }}>
                <img src="/icons/cadii-logo-original.png" alt="cadii" crossOrigin="anonymous" style={{ height: "44px", borderRadius: "8px" }} />
                <div style={{ textAlign: "right" }}>
                  <div style={{ color: "#D4A373", fontSize: "11px", fontWeight: "700", letterSpacing: "1.5px", textTransform: "uppercase" }}>{course.replace(" Country Club", " CC")}</div>
                  <div style={{ color: "rgba(255,255,255,0.45)", fontSize: "10px", marginTop: "2px" }}>{round.date || ""} {round.name ? `\u2022 ${round.name}` : ""}</div>
                </div>
              </div>

              {/* Title */}
              <div style={{ textAlign: "center", marginBottom: "18px", position: "relative" }}>
                <div style={{ color: "#D4A373", fontSize: "13px", fontWeight: "800", letterSpacing: "5px", textTransform: "uppercase" }}>NET LEADERBOARD</div>
                <div style={{ color: "rgba(255,255,255,0.35)", fontSize: "10px", marginTop: "4px", letterSpacing: "1px" }}>AJUSTADO POR HANDICAP</div>
                <div style={{ width: "50px", height: "2px", background: "linear-gradient(90deg, transparent, #D4A373, transparent)", margin: "8px auto 0" }} />
              </div>

              {/* Column headers */}
              <div style={{ display: "flex", alignItems: "center", padding: "8px 12px", borderBottom: "2px solid rgba(212,163,115,0.25)", borderLeft: "3px solid transparent" }}>
                <div style={{ width: "29px", color: "rgba(212,163,115,0.7)", fontSize: "9px", fontWeight: "700", letterSpacing: "1px" }}>POS</div>
                <div style={{ flex: 1, color: "rgba(212,163,115,0.7)", fontSize: "9px", fontWeight: "700", letterSpacing: "1px" }}>JUGADOR</div>
                <div style={{ width: "40px", textAlign: "center", color: "rgba(212,163,115,0.7)", fontSize: "9px", fontWeight: "700", letterSpacing: "1px" }}>HCP</div>
                <div style={{ width: "48px", textAlign: "center", color: "rgba(212,163,115,0.7)", fontSize: "9px", fontWeight: "700", letterSpacing: "1px" }}>NET</div>
              </div>

              {/* Player rows */}
              {rankedByNet.map((p, i) => {
                const isLeader = p.position === 1;
                return (
                  <div key={p.player_id} data-testid={`net-leaderboard-row-${i}`} style={{
                    display: "flex", alignItems: "center", padding: "11px 12px",
                    background: i % 2 === 0 ? "rgba(0,0,0,0.2)" : "rgba(0,0,0,0.12)",
                    borderBottom: i < rankedByNet.length - 1 ? "1px solid rgba(255,255,255,0.06)" : "none",
                    borderLeft: isLeader ? "3px solid #D4A373" : "3px solid transparent",
                  }}>
                    <div style={{ width: "29px", color: isLeader ? "#D4A373" : "rgba(255,255,255,0.6)", fontSize: "14px", fontWeight: "800" }}>{p.posLabel}</div>
                    <div style={{ flex: 1, color: isLeader ? "#fff" : "rgba(255,255,255,0.85)", fontSize: "13px", fontWeight: isLeader ? "700" : "500", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", paddingRight: "4px" }}>{p.name}</div>
                    <div style={{ width: "40px", textAlign: "center", color: "rgba(255,255,255,0.45)", fontSize: "13px", fontWeight: "600" }}>{p.handicap}</div>
                    <div style={{ width: "48px", textAlign: "center", color: "#fff", fontSize: "16px", fontWeight: "800" }}>{p.holesPlayed > 0 ? p.net : "-"}</div>
                  </div>
                );
              })}

              {/* Footer */}
              <div style={{ textAlign: "center", marginTop: "12px", padding: "8px 0", borderTop: "1px solid rgba(255,255,255,0.08)" }}>
                <span style={{ color: "rgba(255,255,255,0.3)", fontSize: "10px", letterSpacing: "1px" }}>PAR {coursePar} {"\u2022"} {HOLES.length} HOYOS</span>
              </div>
              <div style={{ textAlign: "center", paddingTop: "6px" }}>
                <div style={{ color: "rgba(255,255,255,0.2)", fontSize: "9px", letterSpacing: "2px" }}>POWERED BY CADII</div>
              </div>
            </div>
          </div>
        )}

        {/* Download + Close */}
        <div className="mt-4 flex gap-2 justify-center">
          <Button onClick={handleDownload} disabled={downloading} data-testid="download-png-btn" className="bg-[#D4A373] hover:bg-[#C4935F] text-white font-bold px-6">
            <Download className="w-4 h-4 mr-2" />
            {downloading ? "Generando..." : "Descargar PNG"}
          </Button>
          <Button onClick={onClose} variant="ghost" className="text-white hover:text-white/80">
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

export default ShareCardModal;
